import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as taskActions from 'features/task-management/duck';
import * as roomActions from 'features/room-builder/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import { manualTimerErrorStrings, manualTimerValidationRules } from 'shared/validations';
import { getTimersTasks, getRoomDTO } from 'features/task-management/selectors';
import {
  Spinner,
  AlertNotice,
  AlertMessage,
  TimeInput,
  PageNotFound,
  PolarQuestion,
  SimpleModal,
  Input,
  TimeZone,
} from 'shared/components';
import { AddPause } from 'features/task-management/index';
import { PermissionsService, ValidationService } from 'shared/services';
import { TimerModel } from 'shared/models';
import { isEmpty, isUndefined, orderBy, omit } from 'lodash';
import { withTranslation } from 'react-i18next';
import moment from 'moment';

import './ManualTimer.scss';

const timerModel = {
  startDateTime: '',
  stopDateTime: '',
};

const getPauseTime = (pauseList) => {
  let totalTime = 0;
  if (pauseList) {
    pauseList.forEach((pause) => {
      const pauseTime = moment(pause.unpauseDateTime).diff(moment(pause.pauseDateTime), 'seconds');
      totalTime = totalTime + Number.parseInt(pauseTime / 60) + (pauseTime % 60 > 0 ? 1 : 0);
    });
  }
  return totalTime;
};

const isTimeChronological = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return true;
  }
  return moment(endTime).diff(moment(startTime)) > 0;
};

class ManualTimer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentTimer: timerModel,
      discardModalOpen: false,
      saveModalOpen: false,
      pauseQuestion: {
        title: 'taskManagement.manualTimer.pauseQuestion',
        initial: '',
        key: 'pauseQuestion',
      },
      showSuccessMessage: '',
      isDirty: false,
      pauseModalOpen: false,
      invalidTimeError: false,
      serverError: false,
      errors: {},
    };

    this.closeDiscardModal = this.closeDiscardModal.bind(this);
    this.onChangeTime = this.onChangeTime.bind(this);
    this.onQuestionChange = this.onQuestionChange.bind(this);
    this.addTimerPause = this.addTimerPause.bind(this);
    this.removeTimerPause = this.removeTimerPause.bind(this);
    this.toggleSaveModal = this.toggleSaveModal.bind(this);
    this.goBack = this.goBack.bind(this);
    this.saveRoomTimer = this.saveRoomTimer.bind(this);
    this.validTimerFields = this.validTimerFields.bind(this);
    this.removeSuccessMessage = this.removeSuccessMessage.bind(this);
    this.validateTime = this.validateTime.bind(this);
    this.toggleWarnings = this.toggleWarnings.bind(this);
  }

  componentDidMount() {
    const { actions, room, timersTasks, roomDTO, tcEvent } = this.props;
    const { roomId, eventId, timerIdx } = this.props.match.params;

    if (isEmpty(tcEvent)) {
      actions.getTcEvent(eventId).then(() => {
        if (!isEmpty(this.props.tcEvent)) {
          actions.getIrregularityTypes({
            cycleId: this.props.tcEvent.testingEvent.adminId.cycleId.id,
          });
          actions.getTasks(this.props.tcEvent.testingEvent.adminId.cycleId.id);
        }
      });
    } else {
      actions.getIrregularityTypes({
        cycleId: tcEvent.testingEvent.adminId.cycleId.id,
      });
      actions.getTasks(tcEvent.testingEvent.adminId.cycleId.id);
    }
    if (!room.id || room.id !== +roomId) {
      actions.getRoom(roomId);
      actions.getRoomStatus(roomId);
      actions.getRoomCompletedTasks(roomId);
    }

    if (timersTasks.length) {
      const warningModel = {};
      if (timersTasks[timerIdx].warning5) {
        warningModel.warning5DateTime = '';
      }
      if (timersTasks[timerIdx].warning30) {
        warningModel.warning30DateTime = '';
      }
      this.setState(() => ({
        currentTimer: {
          ...timerModel,
          ...timersTasks[timerIdx],
        },
      }));
    } else {
      actions.getRoomTimers(roomId);
    }
    if (isEmpty(roomDTO) || roomDTO.id !== +roomId) {
      if (
        PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
        PermissionsService.hasEventManage(eventId) ||
        PermissionsService.hasRoomAccess(eventId)
      ) {
        actions.getRoomsDTO(eventId);
      } else {
        actions.getRoomDTO(roomId);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { timersTasks } = this.props;
    const { timerIdx } = this.props.match.params;

    if (timersTasks.length && prevProps.timersTasks !== timersTasks) {
      const warningModel = {};
      if (timersTasks[timerIdx].warning5) {
        warningModel.warning5DateTime = '';
        warningModel.warning5 = {
          disable: false,
        };
      }
      if (timersTasks[timerIdx].warning30) {
        warningModel.warning30DateTime = '';
        warningModel.warning30 = {
          disable: false,
        };
      }
      this.setState(() => ({
        currentTimer: {
          ...timerModel,
          ...warningModel,
          ...timersTasks[timerIdx],
        },
      }));
    }
  }

  onChangeTime(time, name) {
    this.setState((prevState) => ({
      currentTimer: {
        ...prevState.currentTimer,
        [name]: time,
      },
      errors: {
        ...prevState.errors,
        [name]: '',
      },
      invalidTimeError: false,
      isDirty: true,
    }));
  }

  onQuestionChange(question, option) {
    if (option === 'No') {
      this.setState((prevState) => ({
        currentTimer: {
          ...prevState.currentTimer,
          roomTimerPauseDTOS: [],
        },
      }));
    }
    this.setState((prevState) => ({
      pauseOption: option === 'Yes' ? !!option : false,
      errors: {
        ...prevState.errors,
        pauseOption: '',
      },
      isDirty: true,
    }));
  }

  addTimerPause(pause, edit) {
    const { currentTimer } = this.state;
    let roomTimerPauseDTOS = currentTimer.roomTimerPauseDTOS || [];
    if (edit) {
      const findIdx = roomTimerPauseDTOS.findIndex((p) => p.id === pause.id);
      roomTimerPauseDTOS.splice(findIdx, 1, pause);
    } else {
      roomTimerPauseDTOS.push(pause);
    }

    roomTimerPauseDTOS = orderBy(roomTimerPauseDTOS, 'pauseDateTime');

    this.setState((prevState) => ({
      currentTimer: {
        ...prevState.currentTimer,
        roomTimerPauseDTOS,
      },
      errors: {
        ...prevState.errors,
        addPause: '',
      },
      invalidTimeError: false,
      showSuccessMessage: edit
        ? 'taskManagement.manualTimer.successEditPauseMessage'
        : 'taskManagement.manualTimer.successPauseMessage',
    }));
  }

  removeTimerPause(pause) {
    let roomTimerPauseDTOS = this.state.currentTimer.roomTimerPauseDTOS;
    const findIdx = roomTimerPauseDTOS.findIndex((p) => p.id === pause.id);
    roomTimerPauseDTOS.splice(findIdx, 1);

    this.setState((prevState) => ({
      currentTimer: {
        ...prevState.currentTimer,
        roomTimerPauseDTOS,
      },
      invalidTimeError: false,
      showSuccessMessage: 'taskManagement.manualTimer.successDeletePauseMessage',
    }));
  }

  validateTime(manualTimer) {
    const { roomTimerPauseDTOS, startDateTime, stopDateTime, warning5DateTime, warning30DateTime } =
      manualTimer;
    let errorPause = false;

    //check pauses
    if (roomTimerPauseDTOS?.length && startDateTime && stopDateTime) {
      let prevPause;

      roomTimerPauseDTOS.forEach((pause) => {
        //check pause to be between start and stop timer
        if (
          !isTimeChronological(pause.pauseDateTime, stopDateTime) ||
          !isTimeChronological(startDateTime, pause.pauseDateTime) ||
          !isTimeChronological(pause.unpauseDateTime, stopDateTime) ||
          !isTimeChronological(startDateTime, pause.unpauseDateTime)
        ) {
          errorPause = true;
          return;
        }
        //check warnings to be outside pause
        if (
          (warning5DateTime &&
            isTimeChronological(pause.pauseDateTime, warning5DateTime) &&
            isTimeChronological(warning5DateTime, pause.unpauseDateTime)) ||
          (warning30DateTime &&
            isTimeChronological(pause.pauseDateTime, warning30DateTime) &&
            isTimeChronological(warning30DateTime, pause.unpauseDateTime))
        ) {
          errorPause = true;
          return;
        }
        //check intersection with the previous pause(pauses ordered ascending by start time)
        if (
          prevPause &&
          (!isTimeChronological(prevPause.pauseDateTime, pause.pauseDateTime) ||
            !isTimeChronological(prevPause.pauseDateTime, pause.unpauseDateTime) ||
            !isTimeChronological(prevPause.unpauseDateTime, pause.pauseDateTime) ||
            !isTimeChronological(prevPause.unpauseDateTime, pause.unpauseDateTime))
        ) {
          errorPause = true;
          return;
        }
        prevPause = pause;
      });
    }

    //check chronological order for dates
    return (
      errorPause ||
      !isTimeChronological(startDateTime, warning30DateTime) ||
      !isTimeChronological(startDateTime, warning5DateTime) ||
      !isTimeChronological(startDateTime, stopDateTime) ||
      !isTimeChronological(warning30DateTime, warning5DateTime) ||
      !isTimeChronological(warning30DateTime, stopDateTime) ||
      !isTimeChronological(warning5DateTime, stopDateTime) ||
      getPauseTime(roomTimerPauseDTOS) > moment(stopDateTime).diff(moment(startDateTime), 'minutes')
    );
  }

  validTimerFields(manualTimer) {
    const { pauseOption } = this.state;
    const listToOmit = [
      manualTimer.warning5?.disable ? 'warning5DateTime' : '',
      manualTimer.warning30?.disable ? 'warning30DateTime' : '',
    ];

    let errors = ValidationService.validate(
      omit(manualTimer, listToOmit),
      omit(manualTimerValidationRules, listToOmit),
      omit(manualTimerErrorStrings, listToOmit)
    );

    if (isUndefined(pauseOption)) {
      errors = {
        ...errors,
        pauseOption: ['errors.selectingOption'],
      };
    } else if (pauseOption && !manualTimer.roomTimerPauseDTOS?.length) {
      errors = {
        ...errors,
        addPause: ['errors.addPause'],
      };
    }

    const invalidTimeError = this.validateTime(manualTimer);
    if ((errors && !isEmpty(errors)) || invalidTimeError) {
      this.setState({
        errors: {
          ...errors,
        },
        invalidTimeError,
      });
      return false;
    }

    this.setState({
      invalidTimeError: false,
    });
    return true;
  }

  async saveRoomTimer() {
    const { roomId, eventId } = this.props.match.params;
    const { user, actions } = this.props;
    const { currentTimer } = this.state;

    let timerModel = new TimerModel(currentTimer);
    timerModel = {
      ...timerModel,
      roomId,
      startPersonnelId: user.id,
      stopPersonnelId: user.id,
    };

    const timerCompleted = await actions.setRoomTimer(roomId, timerModel);

    if (timerCompleted && isEmpty(this.props.errorSetTimer)) {
      if (currentTimer.warning30DateTime && !currentTimer.disable30Min) {
        await actions.completeWarningTask(roomId, {
          roomId,
          taskId: currentTimer.warning30.id,
          completeDateTime: currentTimer.warning30DateTime,
          completePersonnelId: user.id,
        });
        if (!isEmpty(this.props.errorCompleteWarning)) {
          this.setState({
            serverError: true,
            saveModalOpen: false,
          });
          return;
        }
      }

      if (currentTimer.warning5DateTime && !currentTimer.disable5Min) {
        await actions.completeWarningTask(roomId, {
          roomId,
          taskId: currentTimer.warning5.id,
          completeDateTime: currentTimer.warning5DateTime,
          completePersonnelId: user.id,
        });
        if (!isEmpty(this.props.errorCompleteWarning)) {
          this.setState({
            serverError: true,
            saveModalOpen: false,
          });
          return;
        }
      }

      Promise.all([
        actions.getRoom(roomId),
        actions.getRoomTimers(roomId),
        actions.getRoomCompletedTasks(roomId),
      ]).then(() => {
        this.props.history.goBack();
        this.props.history.replace(`/test-event/${eventId}/room/${roomId}`, {
          successNotif: this.props.t('taskManagement.manualTimer.successAddTime'),
        });
      });
      return;
    }
    this.setState({
      serverError: true,
      saveModalOpen: false,
    });
  }

  closeDiscardModal(leavePage) {
    if (leavePage) {
      this.props.history.goBack();
    }
    this.setState({
      discardModalOpen: false,
    });
  }

  goBack() {
    if (this.state.isDirty) {
      this.setState({
        discardModalOpen: true,
      });
      return;
    }
    this.props.history.goBack();
  }

  toggleSaveModal(validation) {
    if (validation && !this.validTimerFields(this.state.currentTimer)) {
      window.scrollTo(0, 0);
      return;
    }

    this.setState((prevState) => ({
      saveModalOpen: !prevState.saveModalOpen,
    }));
  }

  removeSuccessMessage() {
    this.setState({
      showSuccessMessage: '',
    });
  }

  toggleWarnings(e) {
    const { name } = e.target;

    this.setState((prevState) => ({
      currentTimer: {
        ...prevState.currentTimer,
        [name]: {
          ...prevState.currentTimer[name],
          disable: !prevState.currentTimer[name].disable,
        },
        [name + 'DateTime']: '',
      },
      errors: {
        ...prevState.errors,
        [name + 'DateTime']: '',
      },
    }));
  }

  render() {
    const { room, roomDTO, irregularityTypes, user } = this.props;
    const { roomId, eventId } = this.props.match.params;
    const {
      currentTimer,
      discardModalOpen,
      saveModalOpen,
      pauseOption,
      pauseQuestion,
      showSuccessMessage,
      errors,
      invalidTimeError,
      serverError,
    } = this.state;

    if (
      !room ||
      room.loading ||
      isEmpty(roomDTO) ||
      isEmpty(currentTimer) ||
      !currentTimer.startTask
    ) {
      return <Spinner />;
    }

    return roomDTO.manualTimerFlag ? (
      <div className="manual-timer-container">
        <h4 className="mb-3">
          {currentTimer.startTask.name} - {this.props.t('taskManagement.manualTimer.manualTiming')}
        </h4>
        <AlertNotice
          message={this.props.t('taskManagement.manualTimer.warningMessage')}
          type="warning"
        />
        {invalidTimeError && (
          <AlertNotice
            message={this.props.t('taskManagement.manualTimer.chronologicalError')}
            type="error"
          />
        )}

        <TimeZone message={this.props.t('timezone.infoMessage')} />

        <div className="time-container">
          <div className="title-container">
            <div className="d-inline-block">
              <svg
                width="20"
                height="24"
                viewBox="0 0 20 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.5 4.52849V3H13.5V1.5C13.5 0.672 12.828 0 12 0H7.5C6.672 0 6 0.672 6 1.5V3H9V4.52849C3.96597 4.91098 4.9125e-06 9.11702 0 14.25L2.25 14.25C2.25 12.246 3.03003 10.3635 4.44598 8.94597C5.80646 7.58551 7.5975 6.81299 9.5115 6.75293L9.00301 14.1135C8.95953 14.7299 9.29553 15 9.74853 15C10.2015 15 10.539 14.7299 10.494 14.1135L9.98547 6.75293C11.8995 6.81299 13.6905 7.58551 15.051 8.94597C16.467 10.3619 17.247 12.246 17.247 14.25C17.247 16.254 16.467 18.1365 15.051 19.5539H15.054C13.638 20.97 11.754 21.75 9.75 21.75V24C15.135 24 19.5 19.635 19.5 14.25C19.5 9.11701 15.534 4.91098 10.5 4.52849ZM8 20L2 16V24L8 20Z"
                  fill="#042E60"
                />
              </svg>
            </div>
            <h5>{this.props.t('taskManagement.manualTimer.startTime')}</h5>
          </div>
          <TimeInput
            dateTime={currentTimer.startDateTime}
            inputName="startDateTime"
            onChange={this.onChangeTime}
            error={errors.startDateTime}
          />
        </div>
        <div className="time-container">
          <div className="title-container">
            <div className="d-inline-block">
              <svg
                width="20"
                height="24"
                viewBox="0 0 20 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.5 4.52849V3H13.5V1.5C13.5 0.672 12.828 0 12 0H7.5C6.672 0 6 0.672 6 1.5V3H9V4.52849C3.96597 4.91098 4.9125e-06 9.11702 0 14.25L2.25 14.25C2.25 12.246 3.03003 10.3635 4.44598 8.94597C5.80646 7.58551 7.5975 6.81299 9.5115 6.75293L9.00301 14.1135C8.95953 14.7299 9.29553 15 9.74853 15C10.2015 15 10.539 14.7299 10.494 14.1135L9.98547 6.75293C11.8995 6.81299 13.6905 7.58551 15.051 8.94597C16.467 10.3619 17.247 12.246 17.247 14.25C17.247 16.254 16.467 18.1365 15.051 19.5539H15.054C13.638 20.97 11.754 21.75 9.75 21.75V24C15.135 24 19.5 19.635 19.5 14.25C19.5 9.11701 15.534 4.91098 10.5 4.52849ZM2.91688 17H0V24.0005H2.91688V17ZM7.00019 17H4.08331V24.0005H7.00019V17Z"
                  fill="#042E60"
                />
              </svg>
            </div>
            <h5>
              {this.props.t('taskManagement.manualTimer.totalPause')}
              {currentTimer.roomTimerPauseDTOS?.length
                ? ` (${getPauseTime(currentTimer.roomTimerPauseDTOS)} min)`
                : ''}
            </h5>
          </div>
          <PolarQuestion
            question={pauseQuestion}
            onChange={this.onQuestionChange}
            errors={errors.pauseOption}
          />
          {pauseOption && (
            <AddPause
              timerPauseList={currentTimer.roomTimerPauseDTOS}
              startDateTime={currentTimer.startDateTime}
              stopDateTime={currentTimer.stopDateTime}
              irregularityTypes={irregularityTypes}
              addTimerPause={this.addTimerPause}
              removeTimerPause={this.removeTimerPause}
              roomId={roomId}
              eventId={eventId}
              userId={user.id}
              errorNoPause={errors.addPause}
            />
          )}
        </div>
        {currentTimer.warning30 && (
          <div className="time-container">
            <div className="title-container">
              <div className="d-inline-block">
                <svg
                  width="20"
                  height="24"
                  viewBox="0 0 20 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.5 3V4.52849C15.534 4.91098 19.5 9.11701 19.5 14.25C19.5 19.635 15.135 24 9.75 24V21.75C11.754 21.75 13.638 20.97 15.054 19.5539H15.051C16.467 18.1365 17.247 16.254 17.247 14.25C17.247 12.246 16.467 10.3619 15.051 8.94597C13.6905 7.58551 11.8995 6.81299 9.98547 6.75293L10.494 14.1135C10.539 14.7299 10.2015 15 9.74853 15C9.29553 15 8.95953 14.7299 9.00301 14.1135L9.5115 6.75293C7.5975 6.81299 5.80646 7.58551 4.44598 8.94597C3.03003 10.3635 2.25 12.246 2.25 14.25L0 14.25C4.9125e-06 9.11702 3.96597 4.91098 9 4.52849V3H6V1.5C6 0.672 6.672 0 7.5 0H12C12.828 0 13.5 0.672 13.5 1.5V3H10.5ZM4 22H6V24H4V22ZM6 15H4V20H6V15Z"
                    fill="#042E60"
                  />
                </svg>
              </div>
              <h5>{this.props.t('taskManagement.manualTimer.30minWarning')}</h5>
            </div>
            <TimeInput
              dateTime={currentTimer.warning30DateTime}
              inputName="warning30DateTime"
              onChange={this.onChangeTime}
              error={errors.warning30DateTime}
              disabled={currentTimer.warning30.disable}
            />
            <Input
              containerClassName="mb-0 pt-3"
              type="checkbox"
              name="warning30"
              id="warning30"
              checked={currentTimer.warning30.disable}
              onChange={this.toggleWarnings}>
              <label htmlFor="warning30">
                {this.props.t('taskManagement.manualTimer.disableWarningMessage', {
                  nrOfMinutes: 30,
                })}
              </label>
            </Input>
          </div>
        )}
        {currentTimer.warning5 && (
          <div className="time-container">
            <div className="title-container">
              <div className="d-inline-block">
                <svg
                  width="20"
                  height="24"
                  viewBox="0 0 20 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.5 3V4.52849C15.534 4.91098 19.5 9.11701 19.5 14.25C19.5 19.635 15.135 24 9.75 24V21.75C11.754 21.75 13.638 20.97 15.054 19.5539H15.051C16.467 18.1365 17.247 16.254 17.247 14.25C17.247 12.246 16.467 10.3619 15.051 8.94597C13.6905 7.58551 11.8995 6.81299 9.98547 6.75293L10.494 14.1135C10.539 14.7299 10.2015 15 9.74853 15C9.29553 15 8.95953 14.7299 9.00301 14.1135L9.5115 6.75293C7.5975 6.81299 5.80646 7.58551 4.44598 8.94597C3.03003 10.3635 2.25 12.246 2.25 14.25L0 14.25C4.9125e-06 9.11702 3.96597 4.91098 9 4.52849V3H6V1.5C6 0.672 6.672 0 7.5 0H12C12.828 0 13.5 0.672 13.5 1.5V3H10.5ZM4 22H6V24H4V22ZM6 15H4V20H6V15Z"
                    fill="#042E60"
                  />
                </svg>
              </div>
              <h5>{this.props.t('taskManagement.manualTimer.5minWarning')}</h5>
            </div>
            <TimeInput
              dateTime={currentTimer.warning5DateTime}
              inputName="warning5DateTime"
              onChange={this.onChangeTime}
              error={errors.warning5DateTime}
              disabled={currentTimer.warning5.disable}
            />
            <Input
              containerClassName="mb-0 pt-3"
              type="checkbox"
              name="warning5"
              id="warning5"
              checked={currentTimer.warning5.disable}
              onChange={this.toggleWarnings}>
              <label htmlFor="warning5">
                {this.props.t('taskManagement.manualTimer.disableWarningMessage', {
                  nrOfMinutes: 5,
                })}
              </label>
            </Input>
          </div>
        )}
        <div className="time-container">
          <div className="title-container">
            <div className="d-inline-block">
              <svg
                width="20"
                height="24"
                viewBox="0 0 20 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.5 4.52849V3H13.5V1.5C13.5 0.672 12.828 0 12 0H7.5C6.672 0 6 0.672 6 1.5V3H9V4.52849C3.96597 4.91098 4.9125e-06 9.11702 0 14.25L2.25 14.25C2.25 12.246 3.03003 10.3635 4.44598 8.94597C5.80646 7.58551 7.5975 6.81299 9.5115 6.75293L9.00301 14.1135C8.95953 14.7299 9.29553 15 9.74853 15C10.2015 15 10.539 14.7299 10.494 14.1135L9.98547 6.75293C11.8995 6.81299 13.6905 7.58551 15.051 8.94597C16.467 10.3619 17.247 12.246 17.247 14.25C17.247 16.254 16.467 18.1365 15.051 19.5539H15.054C13.638 20.97 11.754 21.75 9.75 21.75V24C15.135 24 19.5 19.635 19.5 14.25C19.5 9.11701 15.534 4.91098 10.5 4.52849ZM7 17H0V24H7V17Z"
                  fill="#042E60"
                />
              </svg>
            </div>
            <h5>{this.props.t('taskManagement.manualTimer.stopTime')}</h5>
          </div>
          <TimeInput
            dateTime={currentTimer.stopDateTime}
            inputName="stopDateTime"
            onChange={this.onChangeTime}
            error={errors.stopDateTime}
          />
        </div>
        {serverError && (
          <AlertNotice message={this.props.t('errors.somethingWentWrong')} type="error" />
        )}
        <div className="buttons-container">
          <button className="btn btn-warn ml-0" onClick={this.goBack}>
            {this.props.t('common.labels.back')}
          </button>
          <button className="btn mr-0 btn-action" onClick={() => this.toggleSaveModal(true)}>
            {this.props.t('common.labels.save')}
          </button>
        </div>

        <SimpleModal
          isOpen={discardModalOpen}
          onCancel={() => this.closeDiscardModal(false)}
          onConfirm={() => this.closeDiscardModal(true)}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('common.backModal.title')}
          message={this.props.t('taskManagement.manualTimer.discardMessage')}
          buttonsReverseOrder
        />
        <SimpleModal
          isOpen={saveModalOpen}
          containerClassName="save-timer-modal"
          onCancel={() => this.toggleSaveModal(false)}
          onConfirm={() => this.saveRoomTimer()}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('taskManagement.manualTimer.saveQuestion')}
          message={this.props.t('taskManagement.manualTimer.saveMessage')}
          icon="error_outline"
        />
        <AlertMessage
          show={!!showSuccessMessage}
          removeMessage={this.removeSuccessMessage}
          message={showSuccessMessage}
        />
      </div>
    ) : (
      <PageNotFound />
    );
  }
}

function mapStateToProps(state) {
  return {
    timersTasks: getTimersTasks(state),
    roomDTO: getRoomDTO(state),
    room: state.roomOptions.room || {},
    irregularityTypes: state.statics.irregularityTypes || [],
    tcEvent: state.tcEvent.currentEvent || {},
    user: state.users.currentUser,
    errorSetTimer: state.taskManagement.errorSetTimer,
    errorCompleteWarning: state.taskManagement.errorCompleteWarning,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...tcEventActions,
        ...taskActions,
        ...roomActions,
        ...staticActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(ManualTimer))
);

ManualTimer.propTypes = {
  t: PropTypes.func.isRequired,
  // Props from react-router (via withRouterLegacy)
  match: PropTypes.shape({
    params: PropTypes.shape({
      roomId: PropTypes.string.isRequired,
      eventId: PropTypes.string.isRequired,
      timerIdx: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    replace: PropTypes.func,
  }).isRequired,

  // Props from mapStateToProps
  timersTasks: PropTypes.arrayOf(
    PropTypes.shape({
      warning5: PropTypes.bool,
      warning30: PropTypes.bool,
      startDateTime: PropTypes.string,
      stopDateTime: PropTypes.string,
      roomTimerPauseDTOS: PropTypes.arrayOf(PropTypes.shape({})),
      startTask: PropTypes.shape({
        name: PropTypes.string,
      }),
    })
  ).isRequired,
  roomDTO: PropTypes.shape({
    manualTimerFlag: PropTypes.bool,
  }),
  room: PropTypes.shape({
    id: PropTypes.number,
    loading: PropTypes.bool,
  }),
  irregularityTypes: PropTypes.arrayOf(PropTypes.shape({})),
  tcEvent: PropTypes.shape({
    testingEvent: PropTypes.shape({
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
    testCenterCycleId: PropTypes.number,
  }),
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  errorSetTimer: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  errorCompleteWarning: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    getTcEvent: PropTypes.func.isRequired,
    getIrregularityTypes: PropTypes.func.isRequired,
    getTasks: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    getRoomStatus: PropTypes.func.isRequired,
    getRoomCompletedTasks: PropTypes.func.isRequired,
    getRoomTimers: PropTypes.func.isRequired,
    getRoomsDTO: PropTypes.func.isRequired,
    getRoomDTO: PropTypes.func.isRequired,
  }).isRequired,
};
